import { useCallback, useMemo } from 'react';
import type { Permission } from '@terra/shared-authorization';
import { PERMISSIONS, permissionSatisfies } from '@terra/shared-authorization';
import { useUserTenants } from './api/useUserTenants';
import { useProjectStore } from '../store/projectStore';

export function usePermissions() {
  const { tenants, isLoading, error, isTerraUser } = useUserTenants();
  const currentTenantId = useProjectStore(
    (state) => state.selectedCompany?.tenantId,
  );

  const currentTenant = useMemo(
    () => tenants.find((tenant) => tenant.id === currentTenantId),
    [tenants, currentTenantId],
  );

  const permissions = useMemo(
    () => currentTenant?.permissions ?? [],
    [currentTenant],
  );

  const hasPermission = useCallback(
    (requiredPermission: Permission) => {
      if (isTerraUser) {
        return true;
      }

      return permissions.some((userPerm) =>
        permissionSatisfies(userPerm as Permission, requiredPermission),
      );
    },
    [permissions, isTerraUser],
  );

  const canManageApplications = useMemo(
    () => hasPermission(PERMISSIONS.MANAGE_APPLICATIONS.name),
    [hasPermission],
  );

  return {
    permissions,
    hasPermission,
    isLoading,
    error,
    isTerraUser,
    canManageApplications,
  };
}
